import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "sonner";
import { ThemeProvider } from "next-themes";
import { TooltipProvider } from "@pureid/ui";
import { StepUpDialog } from "./components/step-up-dialog";
import { ShortcutsOverlay } from "./features/shortcuts/shortcuts-overlay";

// Font stack — IBM Plex loaded via Fontsource (bundler-agnostic raw CSS).
// Registers @font-face rules matching the font-family names in
// packages/ui/src/styles.css. Plex Sans is the interface voice; Plex Mono
// is the data voice (identifiers, timestamps, money) via font-mono.
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource/ibm-plex-mono/600.css";
import "@fontsource/ibm-plex-mono/700.css";

// Design system — tokens, base layer, custom utility classes.
import "@pureid/ui/styles.css";

// Print stylesheet — activated by the compliance page's
// "Export PDF" button (window.print()). Hides the admin shell
// and expands every control row so the exported PDF is the
// posture document end to end.
import "./styles/print.css";

// Generated by @tanstack/router-plugin at dev/build time from src/routes/*.tsx.
// If this file doesn't exist yet, run `pnpm dev` once and it will be created.
import { routeTree } from "./routeTree.gen";

import { createQueryClient } from "./lib/query-client";

const queryClient = createQueryClient();

// Create the router. `context` is made available to every route's
// loader and `beforeLoad` guard — we pass the QueryClient in so route
// loaders can pre-fetch data into the cache before the route renders.
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0, // TanStack Query owns staleness, not the router
});

// Type-safe router registration for useRouter/useNavigate/Link across the app.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing <div id='root'> in index.html");

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem disableTransitionOnChange={false}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={0}>
          <RouterProvider router={router} />
          {/* Mounted once at the root so any mutation anywhere in the
              app can trigger the step-up ceremony via withStepUp(). */}
          <StepUpDialog />
          {/* Keyboard shortcut help — press ? on any page. */}
          <ShortcutsOverlay />
        </TooltipProvider>
        <Toaster richColors position="top-right" />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
